// src/components/Booking/OtpModal.js

import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import './OtpModal.css';

// A simple SVG Icon for visual flair
const ShieldCheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    <path d="m9 12 2 2 4-4"></path>
  </svg>
);


export default function OtpModal({ onVerificationSuccess, onClose }) {
    const [step, setStep] = useState(1);
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    const navigate = useNavigate();
    const REACT_APP_TEST = process.env.REACT_APP_TEST || 'https://api.nownfc.com:5007';

    const handleClose = () => {
        if (onClose) onClose();
        navigate('/login');
    };

    const handlePhoneSubmit = async (e) => {
        e.preventDefault();
        if (phone.length !== 10) {
            Swal.fire('Error', 'Please enter a valid 10-digit phone number.', 'error');
            return;
        }
        setIsLoading(true);
        try {
            const checkRes = await fetch(`${REACT_APP_TEST}/api/auth/check-user`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone }),
            });
            if (!checkRes.ok) throw new Error(`Server error: ${checkRes.status}.`);
            
            const checkData = await checkRes.json();
            if (!checkData.exists) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Not Registered',
                    html: `Phone number <strong>+91 ${phone}</strong> is not registered.<br/>Please sign up first.`,
                    confirmButtonText: 'Go to Sign Up',
                    showCancelButton: true,
                }).then((result) => {
                    if (result.isConfirmed) handleClose();
                });
                setIsLoading(false);
                return;
            }

            const otpRes = await fetch(`${REACT_APP_TEST}/api/auth/otp/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone }),
            });
            const otpData = await otpRes.json();
            if (!otpRes.ok) throw new Error(otpData.message || 'Failed to send OTP.');

            Swal.fire('Success', 'An OTP has been sent to your phone number.', 'success');
            setStep(2);
        } catch (error) {
            Swal.fire('Error', `An error occurred: ${error.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const res = await fetch(`${REACT_APP_TEST}/api/auth/verify-otp-and-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone, otp }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'OTP verification failed.');
            
            const sessionData = { user: data.user, verifiedAt: Date.now() };
            localStorage.setItem('userSession', JSON.stringify(sessionData));
            onVerificationSuccess(data.user);
        } catch (error) {
            Swal.fire('Verification Failed', error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="nfc-modal-overlay" onClick={handleClose}>
            <div className="nfc-modal-content" style={{padding:'35px'}} onClick={(e) => e.stopPropagation()}>
                <button className="nfc-modal-close-btn" onClick={handleClose} aria-label="Close modal">×</button>
                
                <div className="nfc-modal-icon-wrapper">
                    <ShieldCheckIcon />
                </div>
                
                <h2 className="nfc-modal-title">Verify Your Identity</h2>
                
                {step === 1 ? (
                    <>
                        <p className="nfc-modal-subtitle">Please enter your registered 10-digit phone number to receive a verification code.</p>
                        <form className="nfc-modal-form" onSubmit={handlePhoneSubmit}>
                            <div className="nfc-modal-input-group">
                                <span className="nfc-modal-country-code">+91</span>
                                <input 
                                    className="nfc-modal-input nfc-modal-phone-input"
                                    type="tel" 
                                    placeholder="Your mobile number" 
                                    value={phone} 
                                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} 
                                    required 
                                />
                            </div>
                            <button className="nfc-modal-button nfc-modal-submit-btn" type="submit" disabled={isLoading}>
                                {isLoading ? 'Sending...' : 'Send OTP'}
                            </button>
                        </form>
                    </>
                ) : (
                    <>
                        <p className="nfc-modal-subtitle">An OTP was sent to +91 {phone}. Please check your messages and enter the code below.</p>
                        <form className="nfc-modal-form" onSubmit={handleOtpSubmit}>
                            <input 
                                className="nfc-modal-input nfc-modal-otp-input"
                                type="text" 
                                placeholder="_ _ _ _ _ _" 
                                value={otp} 
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} 
                                required 
                                autoFocus
                            />
                            <button className="nfc-modal-button nfc-modal-submit-btn" type="submit" disabled={isLoading}>
                                {isLoading ? 'Verifying...' : 'Verify & Continue'}
                            </button>
                            <button type="button" className="nfc-modal-button nfc-modal-back-btn" onClick={() => setStep(1)}>
                                Change Number
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}